
import React from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface DateRangeFilterProps {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  onFromDateChange: (date: Date | undefined) => void;
  onToDateChange: (date: Date | undefined) => void;
  onClearDates: () => void;
}

const DateRangeFilter = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onClearDates
}: DateRangeFilterProps) => {
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <div className="flex items-center">
        <span className="text-sm font-medium text-foreground mr-2">From:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "flex items-center justify-start text-left font-normal bg-background border-border",
                !fromDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {fromDate ? format(fromDate, "MMM d, yyyy") : "Select date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-popover border border-border" align="start">
            <Calendar
              mode="single"
              selected={fromDate}
              onSelect={onFromDateChange}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
              disabled={(date) => toDate ? date > toDate : false}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center">
        <span className="text-sm font-medium text-foreground mr-2">To:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "flex items-center justify-start text-left font-normal bg-background border-border",
                !toDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {toDate ? format(toDate, "MMM d, yyyy") : "Select date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-popover border border-border" align="start">
            <Calendar
              mode="single"
              selected={toDate}
              onSelect={onToDateChange}
              initialFocus 
              className={cn("p-3 pointer-events-auto")}
              disabled={(date) => fromDate ? date < fromDate : false}
            />
          </PopoverContent>
        </Popover>
      </div>

      {(fromDate || toDate) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearDates}
          className="text-muted-foreground hover:text-foreground"
        >
          Clear
        </Button>
      )}
    </div>
  );
};

export default DateRangeFilter;
